import React, { useState, useCallback, useMemo } from 'react';
import { Button, Steps, Alert, Tooltip, message } from 'antd';
import {
    ArrowLeftOutlined,
    ArrowRightOutlined,
    CheckOutlined,
    CheckCircleOutlined,
    CopyOutlined,
    ExportOutlined,
    InfoCircleOutlined,
} from '@ant-design/icons';

import { Input, Password } from '@/components/Common/UIKit';
import i18n from '@/content';

import styles from './SlackInlineWizard.module.scss';

const { Step } = Steps;

/** Props for the SlackInlineWizard component */
export interface SlackInlineWizardProps {
    /** Callback when wizard completes with credentials */
    onComplete: (credentials: SlackCredentials) => void;
    /** Callback when user wants to go back */
    onBack: () => void;
    /** The manifest JSON content to display (fetched from API) */
    manifestJson: string;
}

/** Slack credentials collected during wizard */
export interface SlackCredentials {
    clientId: string;
    clientSecret: string;
    signingSecret: string;
    botToken: string;
    userToken: string;
}

/** Individual wizard step configuration */
interface WizardStep {
    title: string;
    description: string;
}

const SLACK_API_URL = 'https://api.slack.com/apps';
const NAMESPACE = 'settings.slackWizard';

/**
 * An inline step-by-step setup wizard for configuring the Slack connector.
 * Renders directly in the page instead of a modal.
 */
const SlackInlineWizard: React.FC<SlackInlineWizardProps> = ({
    onComplete,
    onBack,
    manifestJson,
}) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [manifestCopied, setManifestCopied] = useState(false);
    const [credentials, setCredentials] = useState<SlackCredentials>({
        clientId: '',
        clientSecret: '',
        signingSecret: '',
        botToken: '',
        userToken: '',
    });

    const steps: WizardStep[] = useMemo(() => [
        {
            title: i18n.t(`${NAMESPACE}.steps.createApp.title`),
            description: i18n.t(`${NAMESPACE}.steps.createApp.description`),
        },
        {
            title: i18n.t(`${NAMESPACE}.steps.installApp.title`),
            description: i18n.t(`${NAMESPACE}.steps.installApp.description`),
        },
        {
            title: i18n.t(`${NAMESPACE}.steps.collectCredentials.title`),
            description: i18n.t(`${NAMESPACE}.steps.collectCredentials.description`),
        },
    ], []);

    const handleNext = useCallback((): void => {
        if (currentStep < steps.length - 1) {
            setCurrentStep((prev) => prev + 1);
        }
    }, [currentStep, steps.length]);

    const handlePrev = useCallback((): void => {
        if (currentStep > 0) {
            setCurrentStep((prev) => prev - 1);
        }
    }, [currentStep]);

    const handleCredentialChange = useCallback((field: keyof SlackCredentials, value: string): void => {
        setCredentials((prev) => ({
            ...prev,
            [field]: value,
        }));
    }, []);

    const handleCopyManifest = useCallback(async (): Promise<void> => {
        try {
            await navigator.clipboard.writeText(manifestJson);
            setManifestCopied(true);
        } catch {
            message.error(i18n.t(`${NAMESPACE}.copyFailed`));
        }
    }, [manifestJson]);

    const handleComplete = useCallback((): void => {
        onComplete(credentials);
    }, [credentials, onComplete]);

    const isCredentialsValid = useMemo((): boolean => (
        credentials.clientId.trim() !== ''
        && credentials.clientSecret.trim() !== ''
        && credentials.signingSecret.trim() !== ''
        && credentials.botToken.trim() !== ''
        && credentials.userToken.trim() !== ''
    ), [credentials]);

    const openSlackApi = useCallback((): void => {
        window.open(SLACK_API_URL, '_blank', 'noopener,noreferrer');
    }, []);

    /** Render the content for step 1: Create App */
    const renderCreateAppStep = (): React.ReactNode => (
        <div className={styles.stepContent}>
            <div className={styles.stepInstructions}>
                <ol className={styles.instructionsList}>
                    <li>
                        {i18n.t(`${NAMESPACE}.steps.createApp.instruction1`)}
                    </li>
                    <li>
                        {i18n.t(`${NAMESPACE}.steps.createApp.instruction2`)}
                    </li>
                    <li>
                        {i18n.t(`${NAMESPACE}.steps.createApp.instruction3`)}
                    </li>
                    <li>
                        {i18n.t(`${NAMESPACE}.steps.createApp.instruction4`)}
                    </li>
                    <li>
                        {i18n.t(`${NAMESPACE}.steps.createApp.instruction5`)}
                    </li>
                    <li>
                        {i18n.t(`${NAMESPACE}.steps.createApp.instruction6`)}
                    </li>
                </ol>
                <div className={styles.actionButtons}>
                    <Button
                        type="primary"
                        icon={<ExportOutlined />}
                        onClick={openSlackApi}
                        className={styles.externalButton}
                        size="large"
                    >
                        {i18n.t(`${NAMESPACE}.openSlackApi`)}
                    </Button>
                    <Tooltip title={manifestCopied ? '' : i18n.t(`${NAMESPACE}.copyManifest`)}>
                        <Button
                            type={manifestCopied ? 'default' : 'primary'}
                            icon={manifestCopied ? <CheckCircleOutlined /> : <CopyOutlined />}
                            onClick={handleCopyManifest}
                            className={manifestCopied ? styles.copiedButton : styles.externalButton}
                            size="large"
                        >
                            {manifestCopied
                                ? i18n.t(`${NAMESPACE}.manifestCopiedButton`)
                                : i18n.t(`${NAMESPACE}.copyManifestButton`)}
                        </Button>
                    </Tooltip>
                </div>
            </div>
            <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                message={i18n.t(`${NAMESPACE}.steps.createApp.prerequisite`)}
                description={i18n.t(`${NAMESPACE}.steps.createApp.prerequisiteDesc`)}
                className={styles.infoAlert}
            />
        </div>
    );

    /** Render the content for step 2: Install App */
    const renderInstallStep = (): React.ReactNode => (
        <div className={styles.stepContent}>
            <div className={styles.stepInstructions}>
                <h3 className={styles.stepSubtitle}>
                    {i18n.t(`${NAMESPACE}.steps.installApp.subtitle`)}
                </h3>
                <ol className={styles.instructionsList}>
                    <li>
                        {i18n.t(`${NAMESPACE}.steps.installApp.instruction1`)}
                    </li>
                    <li>
                        {i18n.t(`${NAMESPACE}.steps.installApp.instruction2`)}
                    </li>
                    <li>
                        {i18n.t(`${NAMESPACE}.steps.installApp.instruction3`)}
                    </li>
                </ol>
            </div>
            <div className={styles.permissionsSection}>
                <h4 className={styles.permissionsTitle}>
                    {i18n.t(`${NAMESPACE}.steps.installApp.permissionsTitle`)}
                </h4>
                <div className={styles.permissionsGrid}>
                    <div className={styles.permissionCard}>
                        <span className={styles.permissionLabel}>
                            {i18n.t(`${NAMESPACE}.steps.installApp.botScopes`)}
                        </span>
                        <p className={styles.permissionDesc}>
                            {i18n.t(`${NAMESPACE}.steps.installApp.botScopesDesc`)}
                        </p>
                    </div>
                    <div className={styles.permissionCard}>
                        <span className={styles.permissionLabel}>
                            {i18n.t(`${NAMESPACE}.steps.installApp.userScopes`)}
                        </span>
                        <p className={styles.permissionDesc}>
                            {i18n.t(`${NAMESPACE}.steps.installApp.userScopesDesc`)}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    /** Render the content for step 3: Collect Credentials */
    const renderCredentialsStep = (): React.ReactNode => (
        <div className={styles.stepContent}>
            <div className={styles.stepInstructions}>
                <h3 className={styles.stepSubtitle}>
                    {i18n.t(`${NAMESPACE}.steps.collectCredentials.subtitle`)}
                </h3>
                <p className={styles.stepDescription}>
                    {i18n.t(`${NAMESPACE}.steps.collectCredentials.descriptionText`)}
                </p>
            </div>
            <div className={styles.credentialsForm}>
                <div className={styles.credentialGroup}>
                    <h4 className={styles.groupTitle}>
                        {i18n.t(`${NAMESPACE}.steps.collectCredentials.basicInfo`)}
                    </h4>
                    <div className={styles.fieldRow}>
                        <label className={styles.fieldLabel} htmlFor="slack-clientId">
                            {i18n.t('settings.slack.clientId')}
                        </label>
                        <Input
                            id="slack-clientId"
                            value={credentials.clientId}
                            onChange={(e) => handleCredentialChange('clientId', e.target.value)}
                            placeholder={i18n.t(`${NAMESPACE}.placeholders.clientId`)}
                            size="large"
                        />
                    </div>
                    <div className={styles.fieldRow}>
                        <label className={styles.fieldLabel} htmlFor="slack-clientSecret">
                            {i18n.t('settings.slack.clientSecret')}
                        </label>
                        <Password
                            id="slack-clientSecret"
                            value={credentials.clientSecret}
                            onChange={(e) => handleCredentialChange('clientSecret', e.target.value)}
                            placeholder={i18n.t(`${NAMESPACE}.placeholders.clientSecret`)}
                            size="large"
                        />
                    </div>
                    <div className={styles.fieldRow}>
                        <label className={styles.fieldLabel} htmlFor="slack-signingSecret">
                            {i18n.t('settings.slack.signingSecret')}
                        </label>
                        <Password
                            id="slack-signingSecret"
                            value={credentials.signingSecret}
                            onChange={(e) => handleCredentialChange('signingSecret', e.target.value)}
                            placeholder={i18n.t(`${NAMESPACE}.placeholders.signingSecret`)}
                            size="large"
                        />
                    </div>
                </div>
                <div className={styles.credentialGroup}>
                    <h4 className={styles.groupTitle}>
                        {i18n.t(`${NAMESPACE}.steps.collectCredentials.oauthTokens`)}
                    </h4>
                    <div className={styles.fieldRow}>
                        <label className={styles.fieldLabel} htmlFor="slack-userToken">
                            {i18n.t('settings.slack.userToken')}
                        </label>
                        <Password
                            id="slack-userToken"
                            value={credentials.userToken}
                            onChange={(e) => handleCredentialChange('userToken', e.target.value)}
                            placeholder={i18n.t(`${NAMESPACE}.placeholders.userToken`)}
                            size="large"
                        />
                    </div>
                    <div className={styles.fieldRow}>
                        <label className={styles.fieldLabel} htmlFor="slack-botToken">
                            {i18n.t('settings.slack.botToken')}
                        </label>
                        <Password
                            id="slack-botToken"
                            value={credentials.botToken}
                            onChange={(e) => handleCredentialChange('botToken', e.target.value)}
                            placeholder={i18n.t(`${NAMESPACE}.placeholders.botToken`)}
                            size="large"
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    const renderStepContent = (): React.ReactNode => {
        switch (currentStep) {
            case 0:
                return renderCreateAppStep();
            case 1:
                return renderInstallStep();
            case 2:
                return renderCredentialsStep();
            default:
                return null;
        }
    };

    const isLastStep = currentStep === steps.length - 1;

    return (
        <div className={styles.wizard}>
            <div className={styles.header}>
                <div className={styles.headerContent}>
                    <img
                        src="/assets/icons/slack-logo.svg"
                        alt="Slack"
                        className={styles.slackLogo}
                    />
                    <div className={styles.headerText}>
                        <h2 className={styles.title}>
                            {i18n.t(`${NAMESPACE}.title`)}
                        </h2>
                        <p className={styles.subtitle}>
                            {i18n.t(`${NAMESPACE}.subtitle`)}
                        </p>
                    </div>
                </div>
            </div>

            <Steps
                current={currentStep}
                className={styles.steps}
                size="small"
                labelPlacement="vertical"
            >
                {steps.map((step) => (
                    <Step key={step.title} title={step.title} />
                ))}
            </Steps>

            <div className={styles.content}>
                {renderStepContent()}
            </div>

            <div className={styles.footer}>
                <Button
                    onClick={currentStep === 0 ? onBack : handlePrev}
                    icon={<ArrowLeftOutlined />}
                    className={styles.navButton}
                >
                    {currentStep === 0
                        ? i18n.t(`${NAMESPACE}.cancel`)
                        : i18n.t(`${NAMESPACE}.back`)}
                </Button>
                <div className={styles.footerRight}>
                    {isLastStep ? (
                        <Button
                            type="primary"
                            onClick={handleComplete}
                            disabled={!isCredentialsValid}
                            icon={<CheckOutlined />}
                            className={styles.completeButton}
                        >
                            {i18n.t(`${NAMESPACE}.complete`)}
                        </Button>
                    ) : (
                        <Button
                            type="primary"
                            onClick={handleNext}
                            className={styles.nextButton}
                        >
                            {i18n.t(`${NAMESPACE}.next`)}
                            <ArrowRightOutlined />
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SlackInlineWizard;

